import { useForm } from "@inertiajs/react"
import { Modals, useModals } from "@/hooks/useModals"
import Button from "@/components/Button"
import Modal from "@/components/Modal"
import { Player } from "@/types/serializers"
import { PlayerTitleOption } from "@/types/types"
import { titleOptions } from "@/constants"
import ErrorMessage from "@/components/ErrorMessage"

type Inputs = {
  name: string
  title: PlayerTitleOption
  fide_id: string
  elo: string
}

const EditPlayerModal = ({ player }: { player: Player }) => {
  const { activeModal, closeModal } = useModals()
  const isOpen = activeModal === Modals.EditPlayer

  const { data, setData, patch, errors, processing } = useForm<Inputs>({
    name: player.name ?? "",
    title: player.title ?? "",
    fide_id: player.fide_id ?? "",
    elo: player.elo ?? "",
  })

  const savePlayer = async (e: React.FormEvent) => {
    e.preventDefault()

    patch(`/players/${player.id}`, {
      async: true,
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => closeModal(),
    })
  }

  return (
    <Modal show={isOpen} onClose={closeModal} title="Edit player">
      <form onSubmit={savePlayer} className="mt-4 space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm leading-6 font-medium text-gray-900"
          >
            Name
          </label>
          <input
            type="text"
            id="name"
            required
            className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
            value={data.name}
            onChange={(e) => setData("name", e.target.value)}
          />

          <ErrorMessage className="mt-1.5" message={errors.name} />
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-100 px-4 pt-4 pb-4 shadow-inner">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="title"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Title
              </label>
              <select
                id="title"
                className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.title}
                onChange={(e) =>
                  setData("title", e.target.value as PlayerTitleOption)
                }
              >
                {titleOptions.map((title) => (
                  <option key={title}>{title}</option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="fide_id"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                FIDE ID
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="fide_id"
                className="focus:ring-chessdrop-600 hide-spinners mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.fide_id}
                onChange={(e) => setData("fide_id", e.target.value)}
              />
            </div>

            <div>
              <label
                htmlFor="elo"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Elo
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="elo"
                className="focus:ring-chessdrop-600 hide-spinners mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.elo}
                onChange={(e) => setData("elo", e.target.value)}
              />
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            These fields are optional.
          </p>
        </div>

        <Button loading={processing} type="submit" fullWidth>
          Save player
        </Button>
      </form>
    </Modal>
  )
}

export default EditPlayerModal
